import { Link, useOutletContext } from 'react-router-dom';

import { SCANNER_MODES } from './modes';
import {
  describePendingSyncReason,
  type PendingSyncAvailability,
} from './pendingSync';
import { scannerModulePath } from './routes';
import { touchTargetStyle } from './touch';
import { describeOfflineSyncState } from './useOfflineSync';
import type { ScannerOutletContext } from './ScannerShell';
import { isPlaceholderIdentity, readShopFloorIdentity } from '../lib/shopFloor/identity';

/*
 * ข้อความบนแผงออฟไลน์ต้องอ่านได้จากระยะแขนและต้องไม่โกหกสามอย่าง:
 * คิวที่อ่านไม่ได้ไม่ใช่คิวว่าง, ยังไม่รู้ค่าไม่ใช่ศูนย์ และเข้าคิวแล้วยังไม่ใช่บันทึกสำเร็จ
 */
const QUEUE_STATE_LABEL: Record<PendingSyncAvailability, string> = {
  CHECKING: 'กำลังตรวจสอบ',
  READY: 'พร้อมใช้งาน',
  UNAVAILABLE: 'ใช้งานไม่ได้',
};

/**
 * ScannerMenu — เมนูหลักของเครื่องสแกน (`/scanner`, index route ใต้ `ScannerShell`)
 *
 * D-109: สิ่งที่เห็นเมื่อเปิด `/scanner` คือปุ่มเลือกโมดูลใหญ่พร้อมคำอธิบาย และ
 * แผงสถานะออฟไลน์ + ปุ่ม "ซิงค์ตอนนี้" เท่านั้น — **ไม่มีฟอร์มของโมดูลใดอยู่ที่นี่
 * เลย** ฟอร์มของโมดูลที่เลือกอยู่ที่ `ScannerShell.ScannerModuleScreen` ผ่าน route
 * ย่อยของตัวเอง กดปุ่มหนึ่งแล้วเมนูทั้งหมด (รวมปุ่มของโมดูลอื่น) หายไปจากจอ
 * เพราะเป็นคนละหน้ากับโมดูลจริง ๆ ไม่ใช่ state ที่สลับกันในหน้าเดียว
 *
 * แผงออฟไลน์อยู่ที่เมนู ไม่ซ่อนหลังปุ่มใด ๆ เพราะ "งานที่กดไปแล้วปลอดภัยไหม" เป็น
 * คำถามระดับกะ ไม่ใช่ของโมดูลใดโมดูลหนึ่ง (D-109 ข้อ 3)
 */
export function ScannerMenu() {
  const { pendingSync, offlineSync } = useOutletContext<ScannerOutletContext>();
  const queueUnavailable = pendingSync.availability === 'UNAVAILABLE';
  const syncing = offlineSync.state.status === 'syncing';
  /* อุปกรณ์ที่ยังไม่ได้ลงทะเบียนจะถูกเซิร์ฟเวอร์ปฏิเสธทั้งชุด — บอกก่อนกด ดีกว่าให้เดา */
  const unregisteredDevice = isPlaceholderIdentity(readShopFloorIdentity());

  return (
    <>
      <section aria-label="เลือกงานที่จะสแกน" className="cap-scanner__modes">
        <h1 className="cap-scanner__heading">เลือกงาน</h1>
        <div className="cap-scanner__mode-list" role="group" aria-label="โหมดการสแกน">
          {SCANNER_MODES.map((option) => (
            <Link
              key={option.id}
              className="cap-scanner__mode"
              style={touchTargetStyle}
              data-mode={option.id}
              to={scannerModulePath(option.path)}
            >
              <span className="cap-scanner__mode-icon" aria-hidden="true">
                {option.icon}
              </span>
              <span className="cap-scanner__mode-step">{option.step}</span>
              <span className="cap-scanner__mode-label">{option.label}</span>
              <span className="cap-scanner__mode-desc">{option.description}</span>
            </Link>
          ))}
        </div>
      </section>

      <section aria-label="สถานะการทำงานออฟไลน์" className="cap-scanner__panel">
        <h2 className="cap-scanner__panel-title">การทำงานออฟไลน์</h2>
        <dl className="cap-scanner__facts">
          <div>
            <dt>คิวคำสั่งออฟไลน์</dt>
            <dd data-testid="queue-availability">{QUEUE_STATE_LABEL[pendingSync.availability]}</dd>
          </div>
          <div>
            <dt>คำสั่งรอซิงค์</dt>
            <dd data-testid="queue-pending-count">{pendingSync.count ?? 'ยังไม่ทราบ'}</dd>
          </div>
          <div>
            <dt>เซิร์ฟเวอร์รับแล้ว</dt>
            <dd data-testid="queue-accepted-count">{pendingSync.accepted ?? 'ยังไม่ทราบ'}</dd>
          </div>
          <div>
            <dt>ต้องมีคนมาดู</dt>
            <dd data-testid="queue-attention-count">{pendingSync.needsAttention ?? 'ยังไม่ทราบ'}</dd>
          </div>
          <div>
            <dt>ผลการบันทึก</dt>
            <dd>เซิร์ฟเวอร์เป็นผู้ตัดสินเสมอ</dd>
          </div>
        </dl>

        {/*
          ปุ่มซิงค์เป็นของผู้ปฏิบัติงาน ไม่ใช่ตัวตั้งเวลา (D-078): คนที่ยืนอยู่หน้าเครื่อง
          รู้ว่าตัวเองเพิ่งเดินออกจากมุมอับสัญญาณ และรู้ว่างานกะนี้ต้องขึ้นก่อนเลิกกะ
          กดได้แม้หัวจอจะบอกว่าออฟไลน์ เพราะ `navigator.onLine` ยืนยันไม่ได้ว่า API
          ตอบจริงหรือไม่ (`useConnectivity.ts`) — ห้ามกันคนไม่ให้ลองด้วยค่าที่เชื่อไม่ได้
        */}
        <button
          type="button"
          className="cap-scanner__sync-now"
          style={touchTargetStyle}
          data-testid="sync-now"
          onClick={() => {
            void offlineSync.syncNow();
          }}
          disabled={syncing}
        >
          {syncing ? 'กำลังซิงค์…' : 'ซิงค์ตอนนี้'}
        </button>
        <p className="cap-scanner__note" data-testid="sync-now-status" role="status">
          {describeOfflineSyncState(offlineSync.state)}
        </p>

        {queueUnavailable ? (
          <p className="cap-scanner__note" data-testid="queue-unavailable-reason" role="alert">
            {describePendingSyncReason(pendingSync.reason)} — ยังใช้หน้าจอได้
            แต่คำสั่งใหม่จะเก็บลงเครื่องไม่ได้ และจำนวนที่ค้างอยู่ตอนนี้ยังไม่ทราบ
          </p>
        ) : null}
        {unregisteredDevice ? (
          <p className="cap-scanner__note" data-testid="sync-device-notice">
            เครื่องนี้ยังไม่ได้ลงทะเบียนกับระบบ เซิร์ฟเวอร์จึงยังไม่รับคำสั่งจากเครื่องนี้
            — คำสั่งที่สแกนไว้ยังอยู่ในเครื่องครบและจะถูกส่งเมื่อลงทะเบียนแล้ว
          </p>
        ) : null}
        <p className="cap-scanner__note">
          คำสั่งที่บันทึกตอนออฟไลน์อยู่ในสถานะ PENDING_LOCAL จนกว่าเซิร์ฟเวอร์จะรับ
          ไม่ถือว่าโพสต์สำเร็จทันที และคำสั่งที่ชนกฎหรือถูกปฏิเสธจะค้างไว้ให้คนมาตัดสิน
          ตัวส่งนี้ไม่แก้ให้เอง
        </p>
      </section>
    </>
  );
}
